import {Injectable} from "@angular/core";
import {
    ActivityGroupId,
    AuditActivityId,
    AuditFindingId,
    AuditId,
    AuditTemplateId,
    ProjectId,
    ReportSpecId,
    SpecId,
    SpecRef,
    FindingId,
    UserId, AuditAutomationRunId, TestObjectId, TestRunId
} from '@webmate-web-core/shared/types/ids';
import {ApiSourcedPaginationRequest, GenericApiResponse} from "@webmate-web-core/shared/api/apibase.api";
import {AuditApi} from "@webmate-web-core/shared/api/rest/audit.restapi";
import {forkJoin, Observable, of, ReplaySubject, Subject} from "rxjs";
import {filter, map, switchMap, take, tap} from "rxjs/operators";
import {DateTime, Progress} from '@webmate-web-core/shared/types/base-types';
import {Tag} from "@webmate-web-core/shared/types/tag";
import {AuditRequirement} from "@webmate-web-core/shared/tools/audit/audittemplate/shared";
import {isUUID} from "@webmate-web-core/shared/utils/utils";
import {EventsService} from "@webmate-web-core/shared/services/events/events.service";
import {
    AdHocReportSpec,
    AuditActivityUpdate,
    AuditEvaluationStatus,
    AuditExecutionStatus,
    AuditFinding,
    AuditReportConfig,
    AuditReportSpec,
    AuditTemplateUpdateAction,
    AuditUpdateAction,
    FilterSpec,
    GroupingSpec,
} from '@webmate-web-core/shared/types/audit';
import {ContextObservationTarget, deriveObservationEnvironmentFromGenerationSpec, ObservationTarget, SpecFilterSpec, StateObservationTarget, TestObject, TestObjectStateSummary, TestRunStatusInfo} from '@webmate-web-core/shared/types/testmgmt';
import {withResultCache} from "@webmate-web-core/shared/utils/cache-util";
import {ReportExpr, ReportId} from '../../../report/report_types';
import {v5 as uuidv5} from 'uuid';
import {ArtifactService} from '@webmate-web-core/shared/services/artifact.service';
import {TestService} from '@webmate-web-core/shared/services/test.service';
import {AutomationPolicyConfig, BrowserSessionStateArtifact} from '@webmate-web-core/shared/types/browsersession';
import {AuditSession} from '../../../workbench/shared/services/audit-session';
import {ReportParameter} from '../../../report/types';

/**
 * Frontend view model for an audit-owned canonical TestObject reference.
 * It exists only to hydrate raw workflow TestObjectIds with a displayable ObservationTarget.
 */
export interface HydratedAuditTarget {
    testObjectId: TestObjectId;
    target: ObservationTarget;
}

export type PlanningMatrix = { [testObjectId: string]: SpecId[] };

export interface RequirementsWorkflowSpec {
    type: 'RequirementsWorkflowSpec';
    requirements: AuditRequirement[];
    testObjects: HydratedAuditTarget[];
    planningMatrix: PlanningMatrix;
}

export interface AuditQueryResult {
    items: AuditSearchRecord[];
    aggregations: any[];
}

export interface StandardAuditAutomationConfig {
    type: 'StandardAuditAutomationConfig';
    activities?: AuditActivityId[];
    reportConfig?: AuditReportConfig;
    autoCreateReportPdf?: boolean;
    autoShareReport?: boolean;
    autoAssociateFindingsWithAudit?: boolean;
    autoSetEvaluationStatusForAssessmentTasks?: boolean;
    executionStatusForAutomatedActivities?: ActivityExecutionStatus;
    labelsForAutomatedActivities?: Tag[];
    labelsForAutomatedFindings?: Tag[];
    automationPolicy?: AutomationPolicyConfig;
    factRequestParamsOverrides?: Record<string, any>;
}

export interface ActivityInfo {
    latestActivityUpdate: DateTime | undefined;
    numberOfProcessedActivities: number;
    numNotStartedActivities: number;
    numInProgressActivities: number;
    numSkippedActivities: number;
    numFinishedActivities: number;
    participants: Set<UserId>;
    numFailed: number;
    numPassed: number;
    numUndecided: number;
    numNotApplicable: number;
}

export interface AuditSearchRecord {
    id: AuditId;
    title: string;
    template?: AuditTemplateId;
    templateName?: string;
    projectId: string;
    creator: string;
    creationTime: Date;
    auditors: string[];
    executionStatus: AuditExecutionStatus;
    deleted: boolean;
    reportCount: number;
    lastUpdate: Date;
    lastUpdater: string;
    labels: any[];
    version: number;
    evaluationStatus: AuditEvaluationStatus;
    fullTextSearch: string;
    workflowInfo: {numPlannedActivities: number, numTestObjects: number, numSpecs: number};
    findingsInfo: {totalCount: number, issueCount: number, latestFinding: Date, totalLabels: any[], totalSpecNames: string[], totalTestObjectTitles: string[]};
    activityInfo: ActivityInfo;
}

export interface AuditActivitySpec {
    localId: number;
    title: string;
    description: string;
    requirementId: string;
}

export type RelativeTimeFilterSpec = 'pastyear' | 'pastmonth' | 'pastweek' | 'pastday';

export interface AbsoluteTimeFilterSpec {
    from?: DateTime;
    until?: DateTime;
}

export type TimeFilterSpec = RelativeTimeFilterSpec | AbsoluteTimeFilterSpec;

export interface AuditActivitiesFilterSpec {
    timeSpec?: TimeFilterSpec;
    searchText?: string;
    testObjectFilterSpec?: TestObjectId[];
    specFilterSpec?: SpecRef[];
    tagsFilterSpec?: Tag[];
    assessmentTaskEvaluationStatusFilterSpec?: AssessmentEvaluationStatus[];
    executionStatusFilterSpec?: ActivityExecutionStatus[];
    onlyWithResults?: boolean;
}

export type ActivityExecutionStatus = 'notstarted' | 'inprogress' | 'finished' | 'skipped' | 'blocked' | 'waitingforreview';
export type AssessmentEvaluationStatus = 'passed' | 'failed' | 'undecided' | 'notapplicable';

export interface AssessmentTask {
    id: AuditActivityId;
    auditId: AuditId;
    assessedSpec: SpecId;
    testObjectId: TestObjectId;
    hiddenFindings: FindingId[];
    summary: string;
    executionStatus: ActivityExecutionStatus;
    evaluationStatus: AssessmentEvaluationStatus;
    type: 'AssessmentTask';
    needsReification?: boolean; // not in backend
    labels?: Tag[];
    numberOfFindings?: number; // not in backend, only for convenience in the frontend
}

export type AuditActivity = AssessmentTask;

export type WorkflowSpec = RequirementsWorkflowSpec;

export interface ActivityGroup {
    id: ActivityGroupId;
    name: string;
    description?: string;
    parentGroup?: ActivityGroupId;
}

export interface AuditTemplate {
    id: AuditTemplateId;
    name: string;
    description: string;
    labels: Tag[];
    workflowSpec: WorkflowSpec;
    specFilters: { [filterName: string]: SpecFilterSpec}
    isBaseTemplate: boolean;
    useForCreation: boolean; // if true, this template can be used to create new audits,
    lang: string;
}

export interface AuditSpec {
    templateId: AuditTemplateId;
    title: string; // title of audit
    scope: string; // scope of audit, i.e. what is being audited
    specFilterName?: string // name of the spec filter to use, if any
}

export interface RequirementsWorkflow {
    type: 'RequirementsWorkflow';
    spec: RequirementsWorkflowSpec;
}

export type Workflow = RequirementsWorkflow;

export interface ReportSection {
    id: string;
    title: string;
    content: string;
    subsections: ReportSection[];
    numberingOverride: string;
    styleClasses?: string[];
}

export interface AuditReport {
    id: ReportSpecId;
    auditId: AuditId;
    projectId: ProjectId;
    sections: ReportSection[];
    creator: UserId;
    creationTime: DateTime;
    version: number;
    lastUpdateTime: DateTime;
    lastUpdater: UserId;
}

export interface AuditAutomation {
    id: string;
}


export interface AuditAutomationRun {
    id: AuditAutomationRunId;
    auditId: AuditId;
    projectId: ProjectId;
    creator: UserId;
    creationTime: DateTime;
    state: 'created' | 'automating_workflow' | 'creating_report' | 'done' | 'failed' | 'stopping' | 'cancelled' | 'postprocessing_finished';
    progress: Progress; // 0-1
    progressMessage?: string;
    config: StandardAuditAutomationConfig;
    startTime?: DateTime;
    endTime?: DateTime;
    associatedReport?: ReportId;
    associatedTestRuns?: { [testRunId: string]: TestRunStatusInfo };
}

export interface Audit {
    id: AuditId;
    template: AuditTemplateId;
    title: string;
    labels: Tag[];
    scope: string;
    creationTime: DateTime;
    lastModified: DateTime;
    executionStatus: AuditExecutionStatus;
    evaluationStatus: AuditEvaluationStatus;
    endTime: DateTime | undefined;
    creator: UserId;
    workflow: Workflow;
    reports: ReportId[];
    reportParameters: {[parameterName: string]: ReportExpr},
    projectId: ProjectId;
}

/**
 * Provides access to the audit domain in webmate.
 */
@Injectable({
    providedIn: 'root'
})
export class AuditService {
    constructor(private api: AuditApi, private eventService: EventsService, private artifactService: ArtifactService, private testService: TestService) {}

    private auditTemplateRequestsByProject: { [s: string /*ProjectId*/]: ApiSourcedPaginationRequest<AuditTemplate> } = {};

    private auditCache: { [s: AuditId]: ReplaySubject<Audit> } = {};

    private auditActivitiesCache: { [s: AuditActivityId]: ReplaySubject<AuditActivity> } = {}; // cache for audit activities

    private _auditEventsHandleByProject: { [s: string /* ProjectId*/]: Observable<any> } = {}; //events

    private eventForwardingSubjects: { [s: string /* ProjectId*/]: Subject<any> } = {}; //events

    private auditSessions: { [auditId: string]: AuditSession } = {};

    private auditAutomationRuns: { [automationRunId: string]: ReplaySubject<AuditAutomationRun>} = {};

    /**
     * Accept either hydrated frontend targets or raw ids and collapse them to the wire-format
     * TestObjectId list expected by the backend update routes.
     */
    private toTestObjectIds(testObjects: TestObjectId[] | HydratedAuditTarget[]): TestObjectId[] {
        return (testObjects || []).reduce((ids, obj) => {
            const testObjectId = typeof obj === 'string' ? obj : obj.testObjectId || (obj as any).id || obj.target?.testObjectId;
            if (!testObjectId) {
                return ids;
            }

            ids.push(testObjectId);
            return ids;
        }, [] as TestObjectId[]);
    }

    /**
     * Recreate a frontend-only observation target for one canonical TestObject reference.
     *
     * This is not persisted back to the audit workflow. It only gives the UI and workbench a
     * stable evidence/context target shape:
     * - representative state available -> StateObservationTarget
     * - no representative state -> ContextObservationTarget
     */
    private makeSyntheticObservationTarget(testObject: TestObject | undefined, stateSummary: TestObjectStateSummary | undefined, testObjectId: TestObjectId): ObservationTarget {
        const name = testObject?.name || 'Unknown target';
        const description = testObject?.description;
        const labels = [...(testObject?.labels || [])];
        const environment = deriveObservationEnvironmentFromGenerationSpec(testObject?.generationSpec);
        if (stateSummary?.stateId) {
            const target: StateObservationTarget = {
                type: 'StateObservationTarget',
                stateId: stateSummary.stateId,
                name,
                testObjectId,
                ...(environment ? {environment} : {}),
            };
            if (description) {
                target.description = description;
            }
            if (labels.length > 0) {
                target.labels = labels;
            }
            return target;
        }

        const target: ContextObservationTarget = {
            type: 'ContextObservationTarget',
            name,
            testObjectId,
            ...(environment ? {environment} : {}),
        };
        if (description) {
            target.description = description;
        }
        if (labels.length > 0) {
            target.labels = labels;
        }
        return target;
    }

    /**
     * Resolve the newest known state per TestObject and then load the full state artifacts for only
     * those representative states. This keeps the UI policy "one representative state per target"
     * in one place.
     */
    public getRepresentativeStatesByTestObjectIds$(testObjectIds: TestObjectId[]): Observable<Record<TestObjectId, BrowserSessionStateArtifact>> {
        const uniqueIds = [...new Set((testObjectIds || []).filter((id): id is TestObjectId => !!id))];
        if (!uniqueIds.length) {
            return of({});
        }

        return forkJoin(
            uniqueIds.map(testObjectId =>
                this.testService.getTestObjectStates$(testObjectId).pipe(
                    map(states => {
                        const representativeState = [...(states || [])]
                            .sort((a, b) => (b.creationTime || '').localeCompare(a.creationTime || ''))[0];
                        return [testObjectId, representativeState] as const;
                    })
                )
            )
        ).pipe(
            switchMap(entries => {
                const stateSummaryByTestObjectId = Object.fromEntries(entries) as Record<TestObjectId, TestObjectStateSummary | undefined>;
                const representativeStateIds = [...new Set(
                    Object.values(stateSummaryByTestObjectId)
                        .map(summary => summary?.stateId)
                        .filter((stateId): stateId is string => !!stateId)
                )];

                if (!representativeStateIds.length) {
                    return of({});
                }

                return this.artifactService.getArtifacts$(representativeStateIds).pipe(
                    map(artifacts => {
                        const statesByStateId = (artifacts || []).reduce((acc, artifact) => {
                            if (!artifact) {
                                return acc;
                            }
                            acc[artifact.id] = BrowserSessionStateArtifact.fromArtifact(artifact);
                            return acc;
                        }, {} as Record<string, BrowserSessionStateArtifact>);

                        return uniqueIds.reduce((acc, testObjectId) => {
                            const stateId = stateSummaryByTestObjectId[testObjectId]?.stateId;
                            if (stateId && statesByStateId[stateId]) {
                                acc[testObjectId] = statesByStateId[stateId];
                            }
                            return acc;
                        }, {} as Record<TestObjectId, BrowserSessionStateArtifact>);
                    })
                );
            })
        );
    }

    /**
     * Hydrate the backend workflow shape (raw TestObjectId list) into the richer frontend read
     * model used by audit views and the workbench.
     */
    private hydrateTestObjects$(testObjectIds: TestObjectId[]): Observable<HydratedAuditTarget[]> {
        if (!testObjectIds.length) {
            return of([]);
        }

        return forkJoin({
            testObjects: this.testService.getTestObjects$(testObjectIds),
            statesByTestObjectId: forkJoin(
                testObjectIds.map(testObjectId =>
                    this.testService.getTestObjectStates$(testObjectId).pipe(
                        map(states => [testObjectId, states] as const)
                    )
                )
            ).pipe(
                map(entries => Object.fromEntries(entries) as Record<TestObjectId, TestObjectStateSummary[]>)
            )
        }).pipe(
            map(({testObjects, statesByTestObjectId}) => {
                const testObjectsById = testObjects.reduce((acc, testObject) => {
                    acc[testObject.id] = testObject;
                    return acc;
                }, {} as Record<TestObjectId, TestObject>);

                return testObjectIds.map(testObjectId => {
                    const testObject = testObjectsById[testObjectId];
                    const preferredState = [...(statesByTestObjectId[testObjectId] || [])]
                        .sort((a, b) => (b.creationTime || '').localeCompare(a.creationTime || ''))[0];

                    return {
                        testObjectId,
                        target: this.makeSyntheticObservationTarget(testObject, preferredState, testObjectId),
                    } as HydratedAuditTarget;
                });
            })
        );
    }

    // Backend workflow payloads only persist TestObjectIds. The frontend expands that list back
    // into hydrated targets so existing views can still work with names, labels, and context targets.
    private normalizeRequirementsWorkflowSpec$(workflowSpec: RequirementsWorkflowSpec): Observable<RequirementsWorkflowSpec> {
        const testObjectIds = (workflowSpec.testObjects || []) as unknown as TestObjectId[];
        if (!testObjectIds.length) {
            return of({
                ...workflowSpec,
                testObjects: [],
            });
        }

        return this.hydrateTestObjects$(testObjectIds).pipe(
            map(testObjects => ({
                ...workflowSpec,
                testObjects,
            }))
        );
    }

    // Inverse of normalizeRequirementsWorkflowSpec$: strip the frontend-only hydrated target data
    // back down to the raw TestObjectId list before sending workflow updates to the backend.
    private toWorkflowSpecPayload(workflowSpec: WorkflowSpec): WorkflowSpec {
        if (workflowSpec?.type !== 'RequirementsWorkflowSpec') {
            return workflowSpec;
        }

        return {
            ...workflowSpec,
            testObjects: this.toTestObjectIds(workflowSpec.testObjects) as unknown as HydratedAuditTarget[],
        };
    }

    private normalizeAudit$(audit: Audit): Observable<Audit> {
        if (audit?.workflow?.type !== 'RequirementsWorkflow') {
            return of(audit);
        }

        return this.normalizeRequirementsWorkflowSpec$(audit.workflow.spec).pipe(
            map(spec => ({
                ...audit,
                workflow: {
                    ...audit.workflow,
                    spec,
                }
            }))
        );
    }

    private normalizeAuditTemplate$(template: AuditTemplate): Observable<AuditTemplate> {
        if (template?.workflowSpec?.type !== 'RequirementsWorkflowSpec') {
            return of(template);
        }

        return this.normalizeRequirementsWorkflowSpec$(template.workflowSpec).pipe(
            map(workflowSpec => ({
                ...template,
                workflowSpec,
            }))
        );
    }

    private _registerEventsForProject(projectId: ProjectId): void {
        if (!this.eventForwardingSubjects[projectId]) {
            this.eventForwardingSubjects[projectId] = new Subject<any>();
        }
        if (isUUID(projectId) && this._auditEventsHandleByProject[projectId] === undefined) {
            this._auditEventsHandleByProject[projectId] = this.eventService.getEventsForProject(projectId); //register events!
            this._auditEventsHandleByProject[projectId].pipe(filter(ev => ev.eventType.startsWith("Audit."))).subscribe(
                next => this._handleEvents(next, projectId)
            );
        }
    }

    private _handleEvents(e: any, forProject: ProjectId): void {
        if (e === undefined) return;
        if (!!this.eventForwardingSubjects[forProject]) {
            this.eventForwardingSubjects[forProject].next(e);
        }
        if (e.eventType.startsWith("Audit.Audit.Delete")) {
            if (!!this.auditCache[e['auditId']]) {
                delete this.auditCache[e.auditId];
            }
        } else if (e.eventType.startsWith("Audit.Audit.Delete")) {
            if (!!this.auditCache[e['auditId']]) {
                this.api.getAudit(e['auditId']).pipe(take(1)).subscribe(r => {
                    if (!this.auditCache[r.id]) this.auditCache[r.id] = new ReplaySubject<Audit>(1);
                    this.auditCache[r.id].next(r)
                });
            }
        } else if (e.eventType.startsWith("Audit.AuditTemplate.")) {
                // There is a change on one of the templates => Reload templates
                if (!!this.auditTemplateRequestsByProject[forProject]) {
                    this.auditTemplateRequestsByProject[forProject].fetch();
                }
        } else if (e.eventType.startsWith("Audit.AuditActivity.")) {
            if (e['activityIds'] && e['activityIds'].length > 0) {
                // There is a change on one of the activities => Reload the activities
                e['activityIds'].forEach((activityId: AuditActivityId) => {
                    if (this.auditActivitiesCache[activityId] === undefined) {
                        this.auditActivitiesCache[activityId] = new ReplaySubject<AuditActivity>(1);
                    }
                    this.api.getAuditActivity(activityId).pipe(take(1)).subscribe(activity => {
                        this.auditActivitiesCache[activityId].next(activity);
                    });
                });
            }
        } else if (e.eventType.startsWith("Audit.AutomationRun.")) {
            if (e['auditId'] && e['automationRunId']) {
                // There is a change on one of the automation runs => Reload the automation run
                this.api.getAuditAutomationRun(e['auditId'], e['automationRunId']).pipe(take(1)).subscribe(run => {
                    if (this.auditAutomationRuns[e['automationRunId']] === undefined) {
                        this.auditAutomationRuns[e['automationRunId']] = new ReplaySubject<AuditAutomationRun>(1);
                    }
                    this.auditAutomationRuns[e['automationRunId']].next(run);
                });
            }

        }
    }


    ///// PUBLIC
    getAuditTemplatesInProject$(projectId: ProjectId): ApiSourcedPaginationRequest<AuditTemplate> {
        let req = this.api.getAuditTemplatesInProject(projectId);
        this.auditTemplateRequestsByProject[projectId] = req;
        return req;
    }

    getAuditTemplate$(id: AuditTemplateId): Observable<AuditTemplate> {
        return this.api.getAuditTemplate(id).pipe(
            switchMap(template => this.normalizeAuditTemplate$(template))
        );
    }

    getOrCreateAuditSession(auditId: AuditId, createNew: boolean = false): AuditSession {
        if (createNew || !(auditId in this.auditSessions)) {
            if (this.auditSessions[auditId]) {
                this.auditSessions[auditId].destroy();
            }
            this.auditSessions[auditId] = new AuditSession(auditId, this, this.artifactService, this.testService);
        }

        return this.auditSessions[auditId];
    }

    deleteAuditTemplate$(id: AuditTemplateId): Observable<void> {
        return this.api.deleteAuditTemplate(id).pipe(map(() => undefined));
    }

    /**
     * Replace the audit template with the given id with the given template.
     */
    updateAuditTemplate$(id: AuditTemplateId, actions: AuditTemplateUpdateAction[]): Observable<void> {
        return this.api.updateAuditTemplate(id, actions).pipe(map(() => undefined));
    }


    duplicateAuditTemplate$(from: AuditTemplateId, targetProject: ProjectId): Observable<AuditTemplateId> {
        return this.api.duplicateAuditTemplate(from, targetProject);
    }

    createAuditTemplate$(projectId: ProjectId, name: string, description: string, workflowSpec: WorkflowSpec): Observable<AuditTemplateId> {
        return this.api.createAuditTemplate(projectId, name, description, this.toWorkflowSpecPayload(workflowSpec));
    }

    /// Audits

    startAudit$(projectId: ProjectId, auditTitle: string, scope: string, templateId: AuditTemplateId, specFilterName?: string): Observable<AuditId> {
        return this.api.startAudit(projectId, auditTitle, scope, templateId, specFilterName);
    }

    getAudit$(auditId: AuditId, forceRefresh: boolean = false): Observable<Audit> {
        return withResultCache(() => this.api.getAudit(auditId).pipe(
            switchMap(audit => this.normalizeAudit$(audit))
        ), this.auditCache, auditId, forceRefresh);
    }

    refreshAudit$(auditId: AuditId): Observable<Audit> {
        if (!this.auditCache[auditId]) {
            this.auditCache[auditId] = new ReplaySubject<Audit>(1);
        }
        return this.api.getAudit(auditId).pipe(
            switchMap(audit => this.normalizeAudit$(audit)),
            take(1),
            tap(audit => this.auditCache[auditId].next(audit))
        );
    }

    startAutomation(auditId: AuditId, config?: StandardAuditAutomationConfig): Observable<AuditAutomationRunId> {
        const postBody: StandardAuditAutomationConfig = config || {
            type: 'StandardAuditAutomationConfig',
        };

        return this.api.startAutomationRun(auditId, postBody);
    }

    updateAudit$(auditId: AuditId, updates: AuditUpdateAction[]): Observable<void> {
        return this.api.updateAudit(auditId, updates).pipe(map(() => undefined));
    }

    startEventPollingForProject(projectId: ProjectId) {
        this._registerEventsForProject(projectId);
    }

    getEventsForProject$(projectId: ProjectId): Observable<any> {
        this._registerEventsForProject(projectId);
        return this.eventForwardingSubjects[projectId].asObservable();
    }

    deleteAudit$(auditId: AuditId): Observable<void> {
        return this.api.deleteAudit(auditId).pipe(map(() => undefined));
    }

    getAuditActivity$(auditActivityId: string): Observable<AuditActivity> {
        if (this.auditActivitiesCache[auditActivityId] === undefined) {
            this.auditActivitiesCache[auditActivityId] = new ReplaySubject<AuditActivity>(1);
            this.api.getAuditActivity(auditActivityId).pipe(
                take(1)
            ).subscribe(activity => {
                this.auditActivitiesCache[auditActivityId].next(activity);
            });
        }
        return this.api.getAuditActivity(auditActivityId);
    }

    getAuditActivitiesOfAudit$(auditId: AuditId): Observable<AuditActivity[]> {
        return this.api.getAuditActivitiesOfAudit(auditId).pipe(
            tap(activities => {
                // Cache each activity in the audit activities cache
                activities.forEach(activity => {
                    if (!this.auditActivitiesCache[activity.id]) {
                        this.auditActivitiesCache[activity.id] = new ReplaySubject<AuditActivity>(1);
                        this.auditActivitiesCache[activity.id].next(activity);
                    }
                });
            })
        );
    }

    queryAuditActivitiesOfAudit$(auditId: AuditId, filterSpec: AuditActivitiesFilterSpec): Observable<AuditActivity[]> {
        return this.api.queryAuditActivitiesOfAudit$(auditId, filterSpec);
    }

    updateAuditFinding(auditActivityId: AuditActivityId, auditFinding: AuditFinding): Observable<GenericApiResponse> {
        return this.api.updateAuditFinding(auditActivityId, auditFinding);
    }


    // report specs

    public queryAdhocAuditReports$(projectId: ProjectId, reportSpec: AdHocReportSpec): Observable<AuditQueryResult> {
        return this.api.queryAdhocAuditReports$(projectId, reportSpec);
    }

    // TODO
    public exportAdhocAuditReportAsCsv$(projectId: ProjectId, reportSpec: AdHocReportSpec): Observable<any> {
        return of(undefined);
    }

    // TODO
    getAuditReportSpecs$(auditId: AuditId): Observable<AuditReportSpec[]> {
        return of([]);
    }

    updateAuditActivity$(id: AuditActivityId, updates: AuditActivityUpdate[]): Observable<AuditActivity> {
        return this.api.updateAuditActivity$(id, updates);
    }

    batchUpdateAuditActivities$(auditId: AuditId, ids: AuditActivityId[], updated: AuditActivityUpdate[]): Observable<GenericApiResponse> {
        return this.api.batchUpdateAuditActivities$(auditId, ids, updated);
    }

    updateTestObjectsOfAudit$(auditId: AuditId, testObjects: TestObjectId[] | HydratedAuditTarget[]): Observable<void> {
        return this.api.updateTestObjectsOfAudit(auditId, this.toTestObjectIds(testObjects)).pipe(map(() => undefined));
    }

    updateTestObjectsOfAuditTemplate$(auditTemplateId: AuditId, testObjects: TestObjectId[] | HydratedAuditTarget[]): Observable<void> {
        return this.api.updateTestObjectsOfAuditTemplate(auditTemplateId, this.toTestObjectIds(testObjects)).pipe(map(() => undefined));
    }

    updateTestObjectPlanningMatrix(auditId: AuditId, matrix: PlanningMatrix): Observable<void> {
        return this.api.updateTestObjectPlanningMatrix(auditId, matrix).pipe(map(() => undefined));
    }

    updateAuditRequirementsOfAudit$(auditId: AuditId, requirements: AuditRequirement[]): Observable<void> {
        return this.api.updateRequirementsOfAudit(auditId, requirements).pipe(map(() => undefined));
    }

    updateAuditReportParameters(auditId: AuditId, parameters: {[parameterName: string]: ReportExpr}): Observable<void> {
        return this.api.updateAudit(auditId, [{type: "SetAuditReportParameters", parameters}]).pipe(map(() => undefined));
    }

    generateAuditReport(auditId: AuditId, reportConfig: AuditReportConfig): Observable<ReportId> {
        return this.api.generateAuditReport(auditId, reportConfig);
    }

    // TODO
    public storeAuditReportSpec(projectId: ProjectId, name: string, filterSpec: FilterSpec, groupingSpec: GroupingSpec, aggregationSpec: GroupingSpec, shared: boolean): Observable<AuditReportSpec | undefined> {
        return of(undefined);
    }

    getAggregatedAuditStatus(evaluationStatus: AuditEvaluationStatus, executionStatus: AuditExecutionStatus): string {
        if (executionStatus === 'completed') {
            if (evaluationStatus === 'passed') {
                return 'passed';
            } else if (evaluationStatus === 'failed') {
                return 'failed';
            } else {
                return 'undecided';
            }
        } else {
            return 'inprogress';
        }
    }

    /**
     * Generates a deterministic UUID (UUIDv5) based on a combination of `testObjectId` and `specId`.
     * This method consistently produces the same UUID for the same input values.
     * Warning: After reification, the UUID may not be valid anymore.
     *
     * @param testObjectId - The unique identifier of the test object.
     * @param specId - The unique identifier of the specification.
     *
     * @returns A UUIDv5 string that uniquely represents the combination of `testObjectId` and `specId`.
     */
    public static generateFlyweightActivityId(testObjectId: TestObjectId, specId: SpecId): AuditActivityId {
        // For UUIDv5 a namespace UUID is needed
        // The UUID 'cadb8334-35a2-4355-ad12-de8fa202adac' was chosen randomly and should help prevent collisions
        // This "namespace" UUID should not change over time, as it is used to generate consistent UUIDs for the same inputs also in the backend.

        const result = uuidv5((testObjectId + '-' + specId), 'cadb8334-35a2-4355-ad12-de8fa202adac');
        return result;
    }

    getAuditAutomationRuns$(auditId: AuditId): Observable<AuditAutomationRun[]> {
        return this.api.getAuditAutomationRuns(auditId);
    }

    getAuditAutomationRun$(auditId: AuditId, automationRunId: AuditAutomationRunId): Observable<AuditAutomationRun> {
        if (this.auditAutomationRuns[automationRunId] === undefined) {
            this.auditAutomationRuns[automationRunId] = new ReplaySubject<AuditAutomationRun>(1);
            this.api.getAuditAutomationRun(auditId, automationRunId).pipe(take(1)).subscribe(run => {
                this.auditAutomationRuns[automationRunId].next(run);
            });
        }
        return this.auditAutomationRuns[automationRunId].asObservable();
    }
}
